import { useMemo } from "react";
import type { z } from "zod";

import type { Prettify } from "@thalamos/common";
import type { WorkflowVersion } from "@thalamos/vision-common";
import {
  createInitialValues,
  FormSectionSchema,
  tk,
  workflows,
} from "@thalamos/vision-common";

import type { MultiPageFormProps } from "../../components/FormBuilder/MultiPageForm/MultiPageForm.js";
import {
  MultiPageForm,
  MultiPageFormSkeleton,
} from "../../components/FormBuilder/MultiPageForm/MultiPageForm.js";
import { Error } from "../../pages/Error/Error.js";

type InformationForHealthcareSchema<T extends WorkflowVersion> = z.infer<
  ReturnType<
    (typeof workflows)[T]["136"]["formDefinition"]["sections"][2]["buildFormSpecification"]
  >["formSchema"]
>;

///

export function InformationForHealthcareFormSkeleton() {
  return (
    <MultiPageFormSkeleton headerText={tk("informationForHealthcare.title")} />
  );
}

///

export type InformationForHealthcareFormProps<T extends WorkflowVersion> =
  Prettify<
    {
      workflowVersion: T;
    } & Pick<
      MultiPageFormProps<InformationForHealthcareSchema<T>>,
      | "initialValues"
      | "incidentWorkItem"
      | "onAutoSave"
      | "onSubmit"
      | "onCancel"
    >
  >;

///

export function InformationForHealthcareForm<T extends WorkflowVersion>(
  props: InformationForHealthcareFormProps<T>,
) {
  const workflow = workflows[props.workflowVersion];
  const section = workflow?.[136].formDefinition.sections.find(
    (section) => section.id === FormSectionSchema.enum.informationForHealthcare,
  );

  const formSpecification = section?.buildFormSpecification({
    incidentWorkItem: props.incidentWorkItem,
  });
  const formSchema = formSpecification?.formSchema;
  const strictFieldSchemas = formSpecification?.strictFieldSchemas;

  const initialValuesOrDefaults = useMemo(() => {
    if (!section || !strictFieldSchemas || !formSchema) {
      return {};
    }
    return createInitialValues(strictFieldSchemas, props.initialValues);
  }, [props, strictFieldSchemas, section, formSchema]);

  if (!section || !formSchema || !strictFieldSchemas) {
    return <Error />;
  }

  return (
    <MultiPageForm<typeof formSchema>
      headerText={tk("informationForHealthcare.title")}
      incidentWorkItem={props.incidentWorkItem}
      schema={formSchema}
      strictFieldSchemas={strictFieldSchemas}
      initialValues={initialValuesOrDefaults}
      pages={formSpecification.pages}
      onCancel={props.onCancel}
      onSubmit={props.onSubmit}
      onAutoSave={props.onAutoSave}
    />
  );
}
